import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/permissions";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Monitor, Smartphone, Tablet, Laptop, X, LogOut, ShieldCheck, MapPin, Clock, Loader2,
} from "lucide-react";

/**
 * Shows every active session for the current user with a "Sign out of all other
 * sessions" primary action. Individual sessions can also be revoked one-by-one.
 */
export default function MySessionsDialog({ open, onOpenChange }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const revokeOne = async (s) => {
    if (s.is_current) return;
    setBusy(true);
    try {
      await api.delete(`/auth/sessions/${s.id}`);
      toast.success("Session signed out");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not revoke session");
    } finally { setBusy(false); }
  };

  const revokeOthers = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      if (data.revoked_count > 0) {
        toast.success(`Signed out of ${data.revoked_count} other session${data.revoked_count === 1 ? "" : "s"}`);
      } else {
        toast.info("No other active sessions to sign out of");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not sign out other sessions");
    } finally { setBusy(false); }
  };

  const others = sessions.filter((s) => !s.is_current);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="my-sessions-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#7C1D3A]" /> My Sessions
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Devices where you are currently signed in to Asset IQ. If you don&apos;t recognise a session, sign it out immediately.
          </p>

          {loading ? (
            <div className="py-10 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">No active sessions.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="sessions-list">
              {sessions.map((s) => (
                <SessionRow key={s.id} session={s} busy={busy} onRevoke={() => revokeOne(s)} />
              ))}
            </div>
          )}

          {others.length > 0 && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <b>{others.length}</b> other session{others.length === 1 ? "" : "s"} active — sign them all out at once with the button below.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-sessions-btn">
            Close
          </Button>
          <Button
            onClick={revokeOthers}
            disabled={busy || others.length === 0}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36] gap-2"
            data-testid="revoke-others-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign out of all other sessions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SessionRow({ session, busy, onRevoke }) {
  const Icon = pickIcon(session.device_label || "");
  return (
    <div className="p-3 flex items-start gap-3" data-testid={`session-row-${session.id}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${session.is_current ? "bg-[#fcf0f3] text-[#7C1D3A]" : "bg-slate-100 text-slate-500"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-medium text-slate-900 text-sm truncate">{session.device_label || "Unknown device"}</div>
          {session.is_current && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
              This device
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
          {session.ip && (
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {session.ip}</span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Last active {formatRelative(session.last_seen_at || session.created_at)}
          </span>
        </div>
      </div>
      {!session.is_current && (
        <button
          onClick={onRevoke}
          disabled={busy}
          className="text-slate-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 disabled:opacity-30"
          data-testid={`revoke-session-${session.id}`}
          title="Sign out this session"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function pickIcon(label) {
  const l = label.toLowerCase();
  if (l.includes("iphone") || l.includes("android phone")) return Smartphone;
  if (l.includes("ipad") || l.includes("tablet")) return Tablet;
  if (l.includes("mac") || l.includes("linux")) return Laptop;
  if (l.includes("windows")) return Monitor;
  return Monitor;
}

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const s = Math.floor(diff / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
    return formatDate(iso);
  } catch { return iso; }
}
